import React, { useState } from "react";
import { Sparkles, Mail, MessageCircle, Facebook, Send } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setBusy(true);
    try {
      // Reuses the existing public contact endpoint if present; otherwise
      // silently degrades to a "we'll email you" toast.
      await api.post("/public/contact", form).catch(() => null);
      toast.success("Message sent. We'll reply within one business hour.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-10 md:grid-cols-2" data-testid="contact-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
          <Sparkles size={12} /> Contact
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
          Talk to a real human.{" "}
          <span className="text-[#FBBF24]">Fast.</span>
        </h1>
        <p className="text-white/70 mt-5 leading-relaxed">
          Whether you&apos;re a prospective partner exploring the platform or an existing member with a
          question about your orders, our support team is here to help.
        </p>

        <ul className="mt-8 space-y-4 text-sm">
          <li className="flex items-start gap-3" data-testid="contact-email">
            <Mail size={18} className="text-[#FBBF24] mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-black">Email</div>
              <a href="mailto:support@zenithfd.online" className="text-white/70 hover:text-[#FBBF24]">support@zenithfd.online</a>
            </div>
          </li>
          <li className="flex items-start gap-3" data-testid="contact-messenger">
            <MessageCircle size={18} className="text-[#FBBF24] mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-black">Live Support Chat</div>
              <div className="text-white/70">Use the chat widget on the bottom-right of any partner page.</div>
            </div>
          </li>
          <li className="flex items-start gap-3" data-testid="contact-social">
            <Facebook size={18} className="text-[#FBBF24] mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-black">Social</div>
              <div className="text-white/70">Follow @zenithflowdynamics on Facebook and TikTok for product drops and partner spotlights.</div>
            </div>
          </li>
        </ul>
      </div>

      <form onSubmit={submit} className="glass-card gold-border p-6 space-y-3" data-testid="contact-form">
        <h2 className="font-display text-xl font-black mb-2">Send us a message</h2>
        <div>
          <label className="zfd-label">Full name</label>
          <input required className="zfd-input" value={form.name} onChange={upd("name")} data-testid="contact-name" />
        </div>
        <div>
          <label className="zfd-label">Email</label>
          <input required type="email" className="zfd-input" value={form.email} onChange={upd("email")} data-testid="contact-email-input" />
        </div>
        <div>
          <label className="zfd-label">Subject</label>
          <input className="zfd-input" value={form.subject} onChange={upd("subject")} data-testid="contact-subject" placeholder="e.g. Question about Business Builder kit" />
        </div>
        <div>
          <label className="zfd-label">Message</label>
          <textarea required rows={5} className="zfd-input" value={form.message} onChange={upd("message")} data-testid="contact-message" />
        </div>
        <button disabled={busy} className="btn-deploy w-full disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="contact-submit">
          <Send size={14} /> {busy ? "Sending…" : "Send Message"}
        </button>
      </form>
    </div>
  );
}
